import axios from 'axios';
import { useEffect, useState } from 'react';
import { useBrand } from './brandStore';
import { useListing } from './listingStore';
import { renderCrop } from './partCrop';
import type { HeroBox } from './realImages';

// The manual's finished-product picture: cropped out of the PDF at the page and box Phase 1's
// read found it at. What "Find the links" matches the shop's photographs against, shown in
// the info table so the crop it will send can be checked. Null while it renders, and when
// the manual pictures no finished product.
export function useHeroCrop(docId: string, file: Blob): string | null {
  const { heroPage, heroBox } = useBrand(docId);
  const [image, setImage] = useState<string | null>(null);

  useEffect(() => {
    if (!heroPage || !heroBox) {
      setImage(null);
      return;
    }
    let alive = true;
    setImage(null);
    void renderCrop(file, heroPage, heroBox).then((url) => {
      if (alive) setImage(url);
    });
    return () => {
      alive = false;
    };
  }, [file, heroPage, heroBox]);

  return image;
}

// The publish route stores the splash as PNG and nothing else (`_write_hero` checks
// the magic bytes), under a 4 MB cap — so a merchant's JPEG has to be re-encoded
// before it can be the splash, and downscaled while it is at it, because a full-size
// photo re-encoded to PNG sails past that cap and would be dropped at publish without
// a word.
const HERO_MAX_SIDE = 1200;

// Crop a data URL to `box` (0..1 from its top-left; null for the whole picture) and
// re-encode it as a PNG data URL fitting HERO_MAX_SIDE. One draw does both: cropping
// and downscaling are the same drawImage, and encoding twice would cost a full-size
// PNG of a photo for nothing.
//
// This works on a canvas precisely because the backend inlined the bytes: drawing a
// cross-origin image would taint the canvas and make toDataURL throw.
function toPng(dataUrl: string, box: HeroBox | null): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const [x0, y0, x1, y1] = box ?? [0, 0, 1, 1];
      const sx = x0 * img.width;
      const sy = y0 * img.height;
      const sw = (x1 - x0) * img.width;
      const sh = (y1 - y0) * img.height;
      if (sw < 1 || sh < 1) {
        resolve(null);
        return;
      }
      const scale = Math.min(1, HERO_MAX_SIDE / Math.max(sw, sh));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(sw * scale));
      canvas.height = Math.max(1, Math.round(sh * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      try {
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

// The state of fetching one gallery picture's bytes: 'loading' while the request is in
// flight, 'ok' with the inlined bytes, 'error' when they wouldn't come (or timed out).
// The three are distinct on purpose — a caller must be able to tell "still loading" from
// "will never load", or it spins forever on a picture the backend can't fetch.
export type ListingBytes = { bytes: string | null; loading: boolean; failed: boolean };

// How long to wait for the backend to fetch and inline one picture before calling it
// dead. Without a cap the request hangs indefinitely when the merchant never answers —
// which is exactly the splash-spins-forever bug this replaces.
const LISTING_IMAGE_TIMEOUT_MS = 25_000;

// The chosen gallery picture's bytes, inlined by the backend so a canvas may read them,
// with whether it is still loading or has failed for good.
//
// Fetched here rather than shipped with the listing read: only ONE of the ten is ever
// cropped, and which one changes every time the user clicks a different thumbnail.
// Inlining all of them up-front would push megabytes of base64 through every listing
// read to use one.
export function useListingBytes(heroUrl: string): ListingBytes {
  const [state, setState] = useState<ListingBytes>({ bytes: null, loading: false, failed: false });
  useEffect(() => {
    if (!heroUrl) {
      setState({ bytes: null, loading: false, failed: false });
      return;
    }
    let alive = true;
    setState({ bytes: null, loading: true, failed: false });
    void axios
      .post<{ image: string }>(
        '/api/notice/listing-image', { url: heroUrl }, { timeout: LISTING_IMAGE_TIMEOUT_MS },
      )
      .then(({ data }) => {
        if (!alive) return;
        setState(
          data.image
            ? { bytes: data.image, loading: false, failed: false }
            : { bytes: null, loading: false, failed: true },
        );
      })
      .catch(() => {
        if (alive) setState({ bytes: null, loading: false, failed: true });
      });
    return () => {
      alive = false;
    };
  }, [heroUrl]);
  return state;
}

// One chosen gallery picture (by URL), cropped to its box and re-encoded as a PNG data
// URL. Null while it loads, or when the bytes wouldn't come / there is no picture. The
// building block for both the published splash and Phase 2's per-submodel reference.
export function useHeroSplash(heroUrl: string, heroBox: HeroBox | null): string | null {
  const { bytes } = useListingBytes(heroUrl);
  const [png, setPng] = useState<string | null>(null);

  useEffect(() => {
    if (!bytes) {
      setPng(null);
      return;
    }
    let alive = true;
    setPng(null);
    // Cut exactly the box, with nothing added here: it already carries the model's
    // margin (see cropMargin), and it is the box the user was shown and could move.
    // Growing it again at crop time is what made the outline a lie.
    void toPng(bytes, heroBox).then((url) => {
      if (alive) setPng(url);
    });
    return () => {
      alive = false;
    };
  }, [bytes, heroBox]);

  return png;
}

// THE splash Phase 6 publishes: the representative submodel's chosen picture, cropped.
export function useListingSplash(docId: string): string | null {
  const { heroUrl, heroBox } = useListing(docId);
  return useHeroSplash(heroUrl, heroBox);
}
